/**
 * ItemDetailPage - Unified Item Detail/Edit Page
 *
 * Displays an item in the ItemEditor (3-column layout).
 * Supports all item types: Task, Key Date, Transaction, Note.
 *
 * Sub-item editing replaces the main view (no modals).
 */

import { useState, useCallback, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Spinner } from '@/components/ui';
import { useItemStore } from '@/stores';
import { ItemEditor } from './ItemEditor';
import { SubItemEditorPage } from './SubItemEditorPage';
import {
  loadItemDraft,
  saveItemDraft,
  addPendingSubItem,
  updatePendingSubItem,
} from '@/services';
import type { ItemType, SubItem, CreateSubItemInput } from '@perfin/shared';
import type { PendingSubItem } from '@/services';
import type { ItemFormData } from './components/fields';

// ============================================================================
// TYPES
// ============================================================================

interface SubItemEditState {
  mode: 'edit' | 'create';
  subItem?: SubItem;
  type?: ItemType;
  tempId?: string; // For pending sub-items
  pendingInput?: CreateSubItemInput; // For editing pending sub-items
  parentFormData?: ItemFormData; // Parent's current form values for inheritance
}

// ============================================================================
// COMPONENT
// ============================================================================

export function ItemDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { items, loading, subItemsMap, subscribeToItemSubItems } = useItemStore();

  // Sub-item editing state (replaces main view when set)
  const [subItemEdit, setSubItemEdit] = useState<SubItemEditState | null>(null);

  // Track the draft ID for pending sub-item support
  const [draftId, setDraftId] = useState<string | null>(null);

  // Counter to trigger draft reload in ItemEditor after pending sub-item changes
  const [draftVersion, setDraftVersion] = useState(0);

  // Get mode and type from URL params (for create mode)
  const mode = searchParams.get('mode');
  const typeParam = searchParams.get('type') as ItemType | null;
  const fromParam = searchParams.get('from'); // For smart back button
  const returnToParam = searchParams.get('returnTo'); // Where notifications should return to
  const subItemParam = searchParams.get('subItem'); // Direct sub-item editing
  const isCreateMode = mode === 'create' && !id;

  // Items subscription is handled by AppLayout - no duplicate subscription needed

  // Find the item if in edit mode
  const item = id ? items.find((i) => i.id === id) : undefined;

  // Subscribe to sub-items when subItem param is present
  useEffect(() => {
    if (subItemParam && id && item) {
      const unsubscribe = subscribeToItemSubItems(item.createdBy, id);
      return unsubscribe;
    }
  }, [subItemParam, id, item, subscribeToItemSubItems]);

  // Auto-open sub-item editor when subItem query param is present
  useEffect(() => {
    if (subItemParam && id && !subItemEdit) {
      const subItems = subItemsMap[id] || [];
      const foundSubItem = subItems.find((s) => s.id === subItemParam);
      if (foundSubItem) {
        setSubItemEdit({ mode: 'edit', subItem: foundSubItem });
      }
    }
  }, [subItemParam, id, subItemsMap, subItemEdit]);

  // Handle navigation after save
  const handleSave = () => {
    // Smart navigation: if came from notifications, go back to notifications
    if (fromParam === 'notifications') {
      const returnPath = returnToParam ? `?from=${encodeURIComponent(returnToParam)}` : '';
      navigate(`/notifications${returnPath}`);
    } else if (item) {
      navigate(`/${item.type === 'keyDate' ? 'dates' : item.type + 's'}`);
    } else if (typeParam) {
      navigate(`/${typeParam === 'keyDate' ? 'dates' : typeParam + 's'}`);
    } else {
      navigate('/');
    }
  };

  const handleCancel = () => {
    // Smart back button: if came from notifications, go back to notifications page
    // preserving where notifications should return to
    if (fromParam === 'notifications') {
      const returnPath = returnToParam ? `?from=${encodeURIComponent(returnToParam)}` : '';
      navigate(`/notifications${returnPath}`);
    } else {
      navigate(-1);
    }
  };

  // Sub-item handlers
  const handleEditSubItem = useCallback((subItem: SubItem) => {
    setSubItemEdit({ mode: 'edit', subItem });
  }, []);

  const handleAddSubItem = useCallback((type: ItemType, parentFormData?: ItemFormData) => {
    setSubItemEdit({ mode: 'create', type, parentFormData });
  }, []);

  const handleEditPendingSubItem = useCallback((pending: PendingSubItem) => {
    setSubItemEdit({
      mode: 'create',
      type: pending.input.type,
      tempId: pending.tempId,
      pendingInput: pending.input,
    });
  }, []);

  const handleSubItemBack = useCallback(() => {
    // If opened via query param, navigate to item type's list page
    if (subItemParam && item) {
      const listPath = item.type === 'keyDate' ? '/dates' : `/${item.type}s`;
      navigate(listPath);
    } else {
      setSubItemEdit(null);
    }
  }, [subItemParam, item, navigate]);

  const handleDraftIdChange = useCallback((newDraftId: string) => {
    setDraftId(newDraftId);
  }, []);

  const handleItemCreated = useCallback((itemId: string) => {
    // Replace URL so subsequent operations use the real item ID
    navigate(`/items/${itemId}`, { replace: true });
  }, [navigate]);

  const handleSubItemSave = useCallback((input?: CreateSubItemInput) => {
    // If input is provided, this is a pending sub-item for a new parent
    if (input && draftId) {
      const draft = loadItemDraft(draftId);
      if (draft) {
        // Check if we're editing an existing pending sub-item
        if (subItemEdit?.tempId) {
          updatePendingSubItem(draft, subItemEdit.tempId, input);
        } else {
          addPendingSubItem(draft, input);
        }
        saveItemDraft(draft);
        // Trigger ItemEditor to reload draft
        setDraftVersion((v) => v + 1);
      }
      return; // Stay in sub-item editor (Save only); close signal is onSave() without input
    }
    setSubItemEdit(null);
  }, [draftId, subItemEdit?.tempId]);

  // Loading state - show spinner while items are loading
  if (id && loading && !item) {
    return (
      <div className="h-full flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  // Item not found (edit mode) - only show after loading completes
  if (id && !item && !loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500 mb-4">Item not found</p>
          <Button onClick={() => navigate('/')}>Back to Dashboard</Button>
        </div>
      </div>
    );
  }

  // Create mode without type
  if (isCreateMode && !typeParam) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500 mb-4">Please select an item type</p>
          <div className="flex gap-2 justify-center flex-wrap">
            <Button onClick={() => navigate('/items/new?mode=create&type=task')}>
              New Task
            </Button>
            <Button onClick={() => navigate('/items/new?mode=create&type=keyDate')}>
              New Key Date
            </Button>
            <Button onClick={() => navigate('/items/new?mode=create&type=transaction')}>
              New Transaction
            </Button>
            <Button onClick={() => navigate('/items/new?mode=create&type=note')}>
              New Note
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Sub-item editing mode (replaces main view)
  if (subItemEdit) {
    return (
      <div className="h-full">
        <SubItemEditorPage
          parentItem={item}
          parentFormData={subItemEdit.parentFormData}
          parentId={item?.id}
          parentCreatedBy={item?.createdBy}
          mode={subItemEdit.mode}
          subItem={subItemEdit.subItem}
          defaultType={subItemEdit.type || 'task'}
          pendingInput={subItemEdit.pendingInput}
          onBack={handleSubItemBack}
          onSave={handleSubItemSave}
        />
      </div>
    );
  }

  // Main item editor
  return (
    <div className="h-full">
      <ItemEditor
        item={item}
        initialType={typeParam || 'task'}
        draftId={draftId || undefined}
        draftVersion={draftVersion}
        onCancel={handleCancel}
        onSave={handleSave}
        onEditSubItem={handleEditSubItem}
        onAddSubItem={handleAddSubItem}
        onEditPendingSubItem={handleEditPendingSubItem}
        onDraftIdChange={handleDraftIdChange}
        onItemCreated={handleItemCreated}
      />
    </div>
  );
}
